import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Asset } from '@nyroh/contracts';

import { api, enviarImagem } from '../lib/api';

/**
 * ============================================================================
 * A BIBLIOTECA DE IMAGENS
 * ============================================================================
 *
 * O QUE EXISTIA
 *
 * O formulário de template tinha um botão "enviar" por campo de imagem. Cada
 * clique abria o seletor de arquivo, subia UM arquivo e escrevia a chave
 * naquele campo — e acabava ali. Não havia lista, então:
 *
 *   - usar a mesma foto como capa e na galeria pedia subir o arquivo duas
 *     vezes, gerando dois objetos no R2 com bytes idênticos;
 *   - trocar a imagem de um campo exigia ter o arquivo de novo na máquina;
 *   - não dava para ver o que já tinha sido enviado para aquele cliente.
 *
 * A tabela `assets` guardava tudo isso desde sempre. Faltava alguém ler.
 *
 * COMO ELA FUNCIONA
 *
 * Sobe vários de uma vez, mostra as miniaturas, e colocar no site é um clique
 * na imagem. O mesmo asset pode ir para quantos campos quiser: a biblioteca
 * guarda o OBJETO, o campo guarda uma referência à chave dele.
 */

interface Props {
  clienteId: string;
  /**
   * Qual campo está sendo preenchido agora. `null` = a biblioteca está aberta
   * só para administrar (subir, apagar), sem destino para o clique.
   */
  campoAlvo: string | null;
  /** A chave que o campo alvo já tem, para marcar qual está em uso. */
  chaveAtual?: string;
  aoEscolher: (chaveR2: string) => void;
  aoFechar: () => void;
}

export const BibliotecaDeImagens: React.FC<Props> = ({
  clienteId,
  campoAlvo,
  chaveAtual,
  aoEscolher,
  aoFechar,
}) => {
  const [itens, setItens] = useState<Asset[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(0);
  const [apagando, setApagando] = useState<string | null>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      /*
        Segue o cursor ate o fim, em vez de pedir uma pagina so.

        `limite: 200` e o teto do schema, e a versao anterior parava ali e
        ignorava `proximoCursor`. Passar de 200 imagens nao dava erro nenhum: as
        mais antigas simplesmente sumiam da tela, e quem olhasse concluiria que
        foram apagadas.

        O teto de 20 paginas (4.000 imagens) existe para um cursor que nunca
        avance nao virar laco infinito no navegador — e quando ele e atingido, a
        tela DIZ, em vez de truncar em silencio.
      */
      const todos: Asset[] = [];
      let cursor: string | undefined;
      let paginas = 0;
      let truncou = false;
      do {
        const r = await api.chamar('assets.listar', {
          params: { id: clienteId },
          query: { limite: 200, ...(cursor ? { cursor } : {}) },
        });
        todos.push(...r.itens);
        cursor = r.proximoCursor ?? undefined;
        paginas += 1;
        if (cursor && paginas >= 20) {
          truncou = true;
          break;
        }
      } while (cursor);

      setItens(todos);
      if (truncou) {
        setErro(
          `Mostrando as ${todos.length} imagens mais recentes. Há mais no acervo — ` +
            'apague o que não usa para elas voltarem a caber.',
        );
      }
    } catch (e) {
      console.error('[biblioteca] falha ao listar:', e);
      setErro(e instanceof Error ? e.message : 'Não consegui carregar as imagens.');
    } finally {
      setCarregando(false);
    }
  }, [clienteId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Sobe os arquivos escolhidos.
   *
   * Em série, e não com `Promise.all`: cada upload é um PUT direto no R2 e
   * trinta em paralelo saturam a banda de subida de quem está do outro lado —
   * o efeito prático seria todos falharem por timeout em vez de trinta
   * terminarem um a um. O contador mostra o andamento.
   */
  const aoEscolherArquivos = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // `Array.from(FileList)` cai em `unknown[]` sem a lib DOM.Iterable, que este
    // tsconfig nao carrega. Indexar a FileList e o caminho tipado.
    const lista = e.target.files;
    const arquivos: File[] = lista ? Array.from({ length: lista.length }, (_, i) => lista[i]!) : [];
    if (arquivos.length === 0) return;

    const falhas: string[] = [];
    for (const [i, arquivo] of arquivos.entries()) {
      setEnviando(arquivos.length - i);
      try {
        await enviarImagem(arquivo, { clienteId });
      } catch (err) {
        console.error(`[biblioteca] falha ao subir ${arquivo.name}:`, err);
        falhas.push(arquivo.name);
      }
    }
    setEnviando(0);
    if (arquivoRef.current) arquivoRef.current.value = '';

    // Recarrega mesmo com falha parcial: o que subiu tem de aparecer.
    await carregar();
    if (falhas.length) {
      setErro(
        falhas.length === arquivos.length
          ? 'Nenhuma imagem subiu. Confira a conexão e tente de novo.'
          : `Não subiram: ${falhas.join(', ')}. O resto está na biblioteca.`,
      );
    }
  };

  const apagar = async (a: Asset) => {
    if (!confirm('Apagar esta imagem? Onde ela já estiver no site, vai sumir.')) return;
    setApagando(a.id);
    try {
      await api.chamar('assets.remover', { params: { id: clienteId, assetId: a.id } });
      setItens((v) => v.filter((x) => x.id !== a.id));
    } catch (e) {
      console.error('[biblioteca] falha ao apagar:', e);
      setErro(e instanceof Error ? e.message : 'Não consegui apagar.');
    } finally {
      setApagando(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Biblioteca de imagens"
      onClick={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-xl bg-slate-900 shadow-2xl ring-1 ring-white/10">
        <header className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
          <div className="mr-auto">
            <h2 className="text-base font-semibold text-white">Imagens deste cliente</h2>
            <p className="text-xs text-slate-400">
              {campoAlvo
                ? 'Clique numa imagem para colocá-la no campo.'
                : 'Suba agora; depois é só clicar para usar em qualquer campo.'}
            </p>
          </div>

          <button
            type="button"
            onClick={() => arquivoRef.current?.click()}
            disabled={enviando > 0}
            className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {enviando > 0 ? `Enviando… (${enviando})` : 'Subir imagens'}
          </button>
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/10"
          >
            Fechar
          </button>
        </header>

        <input
          ref={arquivoRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
          multiple
          hidden
          onChange={aoEscolherArquivos}
        />

        {erro && (
          <p className="border-b border-amber-500/30 bg-amber-500/10 px-5 py-3 text-sm text-amber-200">
            {erro}
          </p>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {carregando ? (
            <p className="py-12 text-center text-sm text-slate-400">Carregando…</p>
          ) : itens.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-slate-300">Nenhuma imagem ainda.</p>
              <p className="mt-1 text-xs text-slate-500">
                Suba as fotos uma vez; elas ficam aqui para todos os campos.
              </p>
            </div>
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {itens.map((a) => {
                const emUso = chaveAtual === a.chaveR2;
                return (
                  <li key={a.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => campoAlvo && aoEscolher(a.chaveR2)}
                      disabled={!campoAlvo}
                      title={campoAlvo ? 'Usar esta imagem' : 'Abra a partir de um campo para usar'}
                      className={`block w-full overflow-hidden rounded-lg ring-2 transition ${
                        emUso
                          ? 'ring-indigo-400'
                          : 'ring-transparent hover:ring-white/40 disabled:hover:ring-transparent'
                      } ${campoAlvo ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <img
                        src={a.url}
                        alt=""
                        loading="lazy"
                        className="aspect-square w-full bg-slate-800 object-cover"
                      />
                    </button>

                    {emUso && (
                      <span className="pointer-events-none absolute left-1.5 top-1.5 rounded bg-indigo-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        em uso
                      </span>
                    )}

                    <button
                      type="button"
                      onClick={() => void apagar(a)}
                      disabled={apagando === a.id}
                      aria-label="Apagar imagem"
                      className="absolute right-1.5 top-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[11px] text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100 disabled:opacity-50"
                    >
                      {apagando === a.id ? '…' : '✕'}
                    </button>

                    <p className="mt-1 truncate text-[11px] text-slate-500">
                      {Math.round(a.bytes / 1024)} kB
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
